import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {observer} from 'mobx-react-lite';

import {
    closestCenter,
    Collision,
    CollisionDetection,
    defaultDropAnimationSideEffects,
    DndContext,
    DragOverlay,
    DropAnimation,
    getFirstCollision,
    KeyboardCode,
    KeyboardSensor,
    MeasuringStrategy,
    MouseSensor,
    pointerWithin,
    rectIntersection,
    TouchSensor,
    UniqueIdentifier,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {arrayMove, SortableContext, verticalListSortingStrategy,} from '@dnd-kit/sortable';
import {DragContainerProps} from "../../../../entity/props/widget/drag-container-props";
import {useService} from "../../../../infrastructure/service/service";
import {Section} from "../../../../entity/resource/section";
import {coordinateGetter} from "../../../../shared/document/coordinate-getter";
import {Title} from "../../../../entity/model/title";
import {MovingType} from "../../../../entity/enums/moving-type";
import Container from "../document/Container";
import {Item} from "../document/Item";
import {Items} from "../../../../entity/resource/items";
import {PageOption} from "../../../../entity/enums/page-option";
import UnSectionedDocuments from "../title/UnSectionedDocuments";
import UploadComponent from "./UploadComponent";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import DocumentViewer from "./DocumentViewer";
import DocumentRunner from "../title/DocumentRunner";
import ValidateDocuments from "../title/ValidateDocuments";
import {TitleTemplate} from "../../../../entity/model/title-template";
import SplitDocumentDialog from "../title/SplitDocumentDialog";
import SplitWithAIDocument from "../title/SplitWithAIDocument";
import SectionAccordion from "./SectionAccordion";

import '../../../../assets/css/style.css';
import '../../../../assets/css/widget/drag-container.css';

export const DragContainer = observer((props: DragContainerProps): JSX.Element => {

    const {sectionService, documentService, titleService, titleTemplateService} = useService();

    const [title, setTitle] = useState<Title | undefined>(titleService.getTitle());
    const [items, setItems] = useState<Items>({});
    const [containers, setContainers] = useState<UniqueIdentifier[]>([]);
    const [sectionalContainers, setSectionalContainers] = useState<Section[]>([]);
    const [updatedContainers, setUpdatedContainers] = useState<UniqueIdentifier[]>([]);
    const [activeId, setActiveId] = useState<UniqueIdentifier>('');
    const [clonedItems, setClonedItems] = useState<Items | null>(null);
    const [files, setFiles] = useState();
    const [openSplitDialog, setOpenSplitDialog] = useState<boolean>(false);

    let hoverTimeout: NodeJS.Timeout | null = null;

    const defaultKeyboardCodes = {
      start: ['Space', 'Enter'],
      cancel: ['Escape'],
      end: ['Space', 'Enter'],
    };
    const lastOverId = useRef<UniqueIdentifier | null>(null);
    const recentlyMovedToNewContainer = useRef(false);
    const activationConstraint = {distance: 3};

    const sensors = useSensors(
        useSensor(MouseSensor, {activationConstraint}),
        useSensor(TouchSensor, {activationConstraint}),
        useSensor(KeyboardSensor, {
            coordinateGetter,
            keyboardCodes: {...defaultKeyboardCodes, start: [KeyboardCode.Space]}
        }),
    );

    const dropAnimation: DropAnimation = {
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5'
                }
            }
        })
    };

    useEffect(() => {
        if (titleTemplateService.getTitleTemplate() && !props.project) {
            const updatedItems = sectionService.getItems(titleTemplateService.getTitleTemplate()?.sections ?? []);

            setItems(sectionService.getItems(titleTemplateService.getTitleTemplate()?.sections ?? []));
            setContainers(Object.keys(updatedItems));

            setSectionalContainers(titleTemplateService.getTitleTemplate()?.sections.filter((section: Section) => section.name !== "Unclassified") ?? []);
        }
    }, [titleTemplateService.getTitleTemplate()]);

    useEffect(() => {
        if (props.project) {
            setTitle(titleService.getTitle());

            const updatedItems = sectionService.getItems(titleService.getTitle()?.sections ?? []);
            setItems(updatedItems);

            titleService.getTitle() && setItems(sectionService.getItems(titleService.getTitle()?.sections ?? []));
            setContainers(Object.keys(updatedItems));

            if (updatedItems && Object.keys(updatedItems).length)
                sectionService.setMovableItems(updatedItems);

            setSectionalContainers(titleService.getTitle()?.sections.filter((section: Section) => section.name !== "Unclassified") ?? []);
        }
    }, [titleService.getTitle()]);

    useEffect(() => {
      requestAnimationFrame(() => {recentlyMovedToNewContainer.current = false});
    }, [items]);

    useEffect(() => {
        const moveContainers = async (): Promise<void> => {
            const sections = props.project ? title?.sections ?? [] : titleTemplateService.getTitleTemplate()?.sections ?? [];
            const sectionMap = new Map(sections.filter(section => section.name !== "Unclassified").map(section => [section.id, section]));
            const sortedSections = updatedContainers.map(id => sectionMap.get(id.toString())).filter((section): section is Section => !!section);
            setSectionalContainers(sortedSections);

            let response;
            if (props.project) {
                response = await sectionService.moveSection(props.project.createdByOrganizationID, updatedContainers, title?.id);
                if (response && typeof response === 'object')
                    titleService.setTitle(response as Title);
            } else {
                const template = titleTemplateService.getTitleTemplate();
                response = await sectionService.moveSection(template?.createdByOrganizationID ?? '', updatedContainers, undefined, template?.id);
            }

            if (response) {
                setActiveId('');
                sectionService.setSectionMoving(false);
                sectionService.setMovingSectionId('');
            }
        }

          if (updatedContainers.length > 0)
            moveContainers();
    }, [updatedContainers]);

    useEffect(() => {
        const init = async (): Promise<void> => {
            if (titleService.getPageOption() === PageOption.SPLIT_PDF)
                setOpenSplitDialog(true);
        }

        init();
    }, [titleService.getPageOption()]);

    const findContainer = (id: UniqueIdentifier): string | undefined => {
        if (id in items)
            return id as string;

        return Object.keys(items).find((key) => items[key].includes(id));
    }

    const initialContainer = useMemo(() => (activeId ? findContainer(activeId) : null), [activeId]);

    const collisionDetectionStrategy: CollisionDetection = useCallback((args): Collision[] => {
        const { activeId }: any = args;

        if (activeId && activeId in items) {
            return closestCenter({
                ...args,
                droppableContainers: args.droppableContainers
            });
        }

        const pointerIntersections = pointerWithin(args);
        const intersections = pointerIntersections.length > 0 ? pointerIntersections : rectIntersection(args);

        let overId = getFirstCollision(intersections, 'id');

        if (overId != null) {
            if (overId in items) {
                const containerItems = items[overId];
                if (containerItems.length > 0) {
                    overId = closestCenter({
                        ...args,
                        droppableContainers: args.droppableContainers.filter(
                            container => containerItems.includes(container.id)
                        ),
                    })[0]?.id || overId;
                }
            }

            lastOverId.current = overId;
            return [{ id: overId }];
        }

        return lastOverId.current ? [{ id: lastOverId.current }] : [];
    }, [activeId, items])

    const onDragCancel = (): void => {
      if (clonedItems)
        setItems(clonedItems);

      setActiveId('');
      setClonedItems(null);
    }

    const findSectionIdByDocumentId = async(id: UniqueIdentifier): Promise<string | undefined> => {
      let sections = props.project ? title?.sections ?? [] : titleTemplateService.getTitleTemplate()?.sections ?? [];
      if (sections && sections.length > 0) {
        for (const section of sections)
          if (section.documents.some((doc) => doc.id === id))
            return section.id;

        return sections.find((sec: Section) => sec.name === "Unclassified")?.id;
      }
    }

    const updateDocumentMoving = async (selectedIds: UniqueIdentifier[], overId: UniqueIdentifier, overIndex: number): Promise<void> => {
      const overContainer = findContainer(overId);
      const activeContainer = await findSectionIdByDocumentId(activeId);
      let index = 0;

      if (activeContainer) {
          for (const id in selectedIds) {

              index++;
              let response;
              if (props.project) {
                  response = await documentService.moveDocument(props.project.createdByOrganizationID, selectedIds[id], activeContainer, overContainer ?? '', overIndex, title?.id);
                  if (response && typeof response === 'object') {
                      setTitle(response as Title);

                      if (index === selectedIds.length)
                          titleService.setTitle(response as Title);
                  }
              } else {
                  const template = titleTemplateService.getTitleTemplate();
                  response = await documentService.moveDocument(template?.createdByOrganizationID ?? '', selectedIds[id], activeContainer, overContainer ?? '', overIndex, undefined, template?.id);

                  if (response && typeof response === 'object')
                    titleTemplateService.setTitleTemplate(response as TitleTemplate);
              }

              if (response)
                  if (index === selectedIds.length) {
                      sectionService.setSelectedIds([]);
                      documentService.setDocumentMoving(false);
                      documentService.setMovingDocumentId('');

                      setItems(sectionService.getItems(response.sections));
                      if (items && Object.keys(items).length)
                          sectionService.setMovableItems(items);
                  }
          }
      }
    }

    const updateMovingStart = async (id: UniqueIdentifier | string): Promise<void> => {
        const movingItemType = sectionService.getMovingType(props.project ? title?.sections ?? [] : titleTemplateService.getTitleTemplate()?.sections ?? [], id);

        if (movingItemType === MovingType.DOCUMENT) {
            documentService.setDocumentMoving(true);
            documentService.setMovingDocumentId(id);
        } else {
            sectionService.setSectionMoving(true);
            sectionService.setMovingSectionId(id);
        }
    }

    const onDragStart = async ({active}: any): Promise<void> => {
        let updatedSelectedIds = sectionService.getSelectedIds().includes(active.id) ? sectionService.getSelectedIds() : [];
        sectionService.setSelectedIds(updatedSelectedIds);

        setActiveId(active.id);
        setClonedItems(items);
        await updateMovingStart(active.id);
    }

    const onDragOver = ({ active, over }: any): void => {
        const overId = over?.id;

        if (!overId || active.id in items)
            return;

        if (hoverTimeout)
            clearTimeout(hoverTimeout);

        hoverTimeout = setTimeout(() => {
            const overContainer = findContainer(overId);
            const activeContainer = findContainer(active.id);

            sectionService.setMovingSectionId(activeContainer || '');
            sectionService.setExpandSingleContainer(true);
            sectionService.addToExpandedSectionIds(overId);

            if (!overContainer || !activeContainer)
                return;

            if (activeContainer !== overContainer) {
                setItems((items: any) => {
                    const activeItems = items[activeContainer];
                    const overItems = items[overContainer];
                    const overIndex = overItems.indexOf(overId);
                    const activeIndex = activeItems.indexOf(active.id);

                    let newIndex: number;

                    if (overId in items) {
                        newIndex = overItems.length + 1;
                    } else {
                        const isBelowOverItem =
                            over &&
                            active.rect.current.translated &&
                            active.rect.current.translated.top > over.rect.top + over.rect.height;

                        const modifier = isBelowOverItem ? 1 : 0;

                        newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
                    }

                    recentlyMovedToNewContainer.current = true;

                    return {
                        ...items,
                        [activeContainer]: items[activeContainer].filter((item: any) => item !== active.id),
                        [overContainer]: [
                            ...items[overContainer].slice(0, newIndex),
                            items[activeContainer][activeIndex],
                            ...items[overContainer].slice(newIndex, items[overContainer].length),
                        ],
                    };
                });
            }

            hoverTimeout = null;
        }, 500);
    }

    const onDragEnd = async ({active, over}: any): Promise<void> => {
        if (hoverTimeout) {
            clearTimeout(hoverTimeout);
            hoverTimeout = null;
            onDragCancel();
            documentService.setDocumentMoving(false);
            return;
        }

        if (active.id in items && over?.id)
            setContainers((prevContainers: UniqueIdentifier[]): UniqueIdentifier[] => {
                const activeIndex = prevContainers.indexOf(active.id);
                const overIndex = prevContainers.indexOf(over.id);

                if (activeIndex !== -1 && overIndex !== -1) {
                    const newContainers = arrayMove(prevContainers, activeIndex, overIndex);
                    setUpdatedContainers(newContainers);
                    return newContainers;
                }
                return prevContainers;
            });
        else {
            const activeContainer = findContainer(active.id);
            const overId = over?.id;

            if (!activeContainer || !overId || !initialContainer) {
                setActiveId('');
                sectionService.setSelectedIds([]);
                return;
            }
            const ids = sectionService.getSelectedIds().length > 0 ? [active.id, ...sectionService.getSelectedIds().filter((id: UniqueIdentifier): boolean => id !== active.id)] : [active.id];
            const overContainer = findContainer(overId);

            if (overContainer) {
                const overItems = sectionService.filterSections(items[overContainer], activeId, ids);
                const overIndex = overItems.indexOf(overId);
                const activeIndex = overItems.indexOf(active.id);
                const newItems = arrayMove(overItems, activeIndex, overIndex);
                const newActiveIndex = newItems.indexOf(active.id);

                setItems((prevItems) => {
                    return {
                        ...prevItems,
                        [initialContainer]: prevItems[initialContainer].filter((id: UniqueIdentifier): boolean => !ids.includes(id)),
                        [activeContainer]: prevItems[activeContainer].filter((id: UniqueIdentifier): boolean => !ids.includes(id)),
                        [overContainer]: [
                            ...newItems.slice(0, newActiveIndex + 1),
                            ...ids.filter((id) => id !== active.id),
                            ...newItems.slice(newActiveIndex + 1),
                        ]
                    };
                });

                await updateDocumentMoving(ids, overId, overIndex);

                sectionService.clearExpandedSectionIds();
                sectionService.addToExpandedSectionIds(overContainer);
            }

            setActiveId('');
        }
    }

    const renderContainerDragOverlay = (containerId: UniqueIdentifier): JSX.Element => {
        return (
            <Container
                project={props.project}
                label={containerId}
                className='draggable-container shadow'
                isNonSection={false}
                setItems={setItems}
                onRemove={false}>
                    {
                        items[containerId]
                            && items[containerId].map((item: any, index: number): JSX.Element => (
                            <Item
                                key={index}
                                project={props.project}
                                parentClassName={'wrapper dragOverlay'}
                                itemClassName={'document-item dragOverlay selected'}
                                dragOverlay={false}
                                handle={false}
                                selected={false}
                                value={item}
                                items={items}
                                setItems={setItems}
                                documentId={item}/>
                        ))
                    }
            </Container>
        );
    }

    const renderSortableItemDragOverlay = (id: UniqueIdentifier): JSX.Element => {
        return (
            <Item
                project={props.project}
                parentClassName={'wrapper dragOverlay count'}
                parentStyle={{'--count': `'${sectionService.getSelectedIds().length == 0 ? 1 : sectionService.getSelectedIds().length}'`} as React.CSSProperties}
                itemClassName={'document-item dragOverlay selected'}
                dragOverlay
                handle={false}
                selected={false}
                value={id}
                items={items}
                setItems={setItems}
                documentId={id}/>
        );
    }

    return (
      <DndContext
          id={'dnd-context'}
          autoScroll={false}
          sensors={sensors}
          collisionDetection={collisionDetectionStrategy}
          measuring={{droppable: {strategy: MeasuringStrategy.Always}}}
          onDragStart={onDragStart}
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}>
          <SortableContext id={'sortable-context'} items={[...containers, 'placeholder']} strategy={verticalListSortingStrategy}>
              {
                  Object.keys(items).length > 0 && containers.length > 0 &&
                      <Grid container spacing={2}>
                          <Grid size={{xs: 12, sm: 12, md: 12, lg: 12, xl: 3}}>
                              <SectionAccordion
                                  project={props.project}
                                  sectionalContainers={sectionalContainers}
                                  setSectionalContainers={setSectionalContainers}
                                  sections={props.project? title?.sections ?? [] : titleTemplateService.getTitleTemplate()?.sections ?? []}
                                  items={items}
                                  titleId={titleService.getTitle()?.id || ''}
                                  activeId={activeId}
                                  setItems={setItems}
                                  nonSection={(props.project ? title?.sections ?? [] : titleTemplateService.getTitleTemplate()?.sections ?? []).find((section: Section): boolean => section.name === 'Unclassified')}/>
                          </Grid>
                          <Grid size={{xs: 12, sm: 12, md: 12, lg: 12, xl: 9}}>
                              {
                                (() => {
                                  switch (titleService.getPageOption()) {
                                      case PageOption.VIEW_PDF:
                                          return (
                                              <Grid className={'grid-row'}>
                                                  <DocumentViewer tab={false}/>
                                              </Grid>
                                          );

                                      case PageOption.DOCUMENTS:
                                      case PageOption.IMPORT:
                                      case PageOption.EXPORT:
                                      case PageOption.DOWNLOAD:
                                      case PageOption.ASSIGN_USERS:
                                      case PageOption.INVOICES:
                                      case PageOption.REPORTS:
                                      case PageOption.LINE_LIST_REPORTS:
                                          return (
                                              <Grid className={'grid-row'}>
                                                  <Box className={'body-box'}>
                                                      <UnSectionedDocuments
                                                          project={props.project}
                                                          nonSection={props.project ? title?.sections.find((section: Section): boolean => section.name === 'Unclassified') : titleTemplateService.getTitleTemplate()?.sections.find((section: Section): boolean => section.name === 'Unclassified')}
                                                          sections={props.project ? title?.sections ?? [] : titleTemplateService.getTitleTemplate()?.sections ?? []}
                                                          items={items ?? {}}
                                                          titleId={titleService.getTitle()?.id ?? ''}
                                                          activeId={activeId ?? ''}
                                                          setItems={setItems}/>
                                                      <UploadComponent
                                                          id={title?.id}
                                                          setFiles={setFiles}
                                                          project={props.project}
                                                          items={items}
                                                          setItems={setItems}/>
                                                  </Box>
                                              </Grid>
                                          );

                                      case PageOption.VALIDATE_DOCUMENTS:
                                          return title ? (
                                              <Grid className={'grid-row'}>
                                                  <ValidateDocuments projectTitle={title}/>
                                              </Grid>
                                          ) : null;

                                      case PageOption.DOCUMENT_RUNNER:
                                          return title ? (
                                              <Grid className={'grid-row'}>
                                                  <DocumentRunner projectTitle={title}/>
                                              </Grid>
                                          ) : null;

                                      case PageOption.SPLIT_WITH_AI:
                                          return title ? (
                                              <Grid className={'grid-row'}>
                                                  <SplitWithAIDocument projectTitle={title}/>
                                              </Grid>
                                          ) : null;

                                      case PageOption.SPLIT_PDF:
                                          return (openSplitDialog && title) ? (
                                              <SplitDocumentDialog
                                                  open={openSplitDialog}
                                                  setOpen={setOpenSplitDialog}
                                                  projectTitle={title}/>
                                          ) : null;

                                      default:
                                          return null;
                                  }
                                })()
                              }
                          </Grid>
                      </Grid>
              }
          </SortableContext>
            {
                createPortal(
                  <DragOverlay dropAnimation={dropAnimation}>
                    {
                        activeId
                            ? containers.includes(activeId)
                                ? renderContainerDragOverlay(activeId)
                                : renderSortableItemDragOverlay(activeId)
                            : null
                    }
                  </DragOverlay>,
                  document.body)}
      </DndContext>
    );
  })
